import React, { useState, useEffect } from "react";
import { Box, Container, Typography, Card, CardMedia, Chip } from "@mui/material";
import SwiperCore, { Autoplay, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import axios from "axios";
import config from "../../config";
import "swiper/css";
import "swiper/css/navigation";

const RecentListings = ({ classes }) => {
  SwiperCore.use([Autoplay]);
  const navigate = useNavigate();
  const [recentListings, setRecentListings] = useState([]);

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const response = await axios.get(
          `${config.SERVER_URL}/api/v1/all_listings?page=1&per_page=12`,
          { headers: { "Content-Type": "application/json" } }
        );
        if (response.status === 200 && response.data.data) {
          setRecentListings(response.data.data);
        }
      } catch (err) {
        // Silently handle
      }
    };
    fetchRecent();
  }, []);

  const getCategoryName = (id) => {
    if (!classes || classes.length === 0) return "";
    const category = classes.find((cat) => cat.id === id);
    return category ? category.name : "";
  };

  if (recentListings.length === 0) return null;

  return (
    <Box sx={{ backgroundColor: "#111", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "22px", md: "30px" },
                color: "#fff",
              }}
            >
              Recently Added
            </Typography>
            <Typography sx={{ color: "#777", fontSize: "14px", mt: 0.3 }}>
              Fresh listings from our community
            </Typography>
          </Box>
          <Typography
            component={Link}
            to="/listings"
            sx={{
              color: "#111",
              fontWeight: 600,
              fontSize: { xs: "12px", md: "14px" },
              textDecoration: "none",
              backgroundColor: "#FFC001",
              px: 2.5,
              py: 0.8,
              borderRadius: "20px",
              "&:hover": { backgroundColor: "#e6ac00" },
            }}
          >
            View All
          </Typography>
        </Box>

        {/* Carousel */}
        <Swiper
          style={{
            "--swiper-navigation-color": "#FFC001",
            paddingBottom: "8px",
          }}
          className="equal-height-swiper"
          spaceBetween={16}
          navigation={true}
          modules={[Navigation]}
          grabCursor={true}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          loop={recentListings.length > 5}
          breakpoints={{
            320: { slidesPerView: 1.2 },
            480: { slidesPerView: 1.5 },
            600: { slidesPerView: 2.2 },
            900: { slidesPerView: 3 },
            1200: { slidesPerView: 4 },
            1500: { slidesPerView: 5 },
          }}
        >
          {recentListings.map((item) => (
            <SwiperSlide key={item.id}>
              <Card
                onClick={() => navigate(`/listing/${item.id}`)}
                sx={{
                  backgroundColor: "#1a1a1a",
                  borderRadius: "14px",
                  cursor: "pointer",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s ease",
                  border: "1px solid transparent",
                  "&:hover": {
                    borderColor: "#FFC001",
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 25px rgba(255, 192, 1, 0.12)",
                  },
                }}
              >
                {/* Image with overlays */}
                <Box sx={{ position: "relative", overflow: "hidden" }}>
                  <CardMedia
                    sx={{
                      height: 220,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      transition: "transform 0.4s ease",
                      ".MuiCard-root:hover &": { transform: "scale(1.05)" },
                    }}
                    image={item?.media?.[0]?.url}
                    title={item?.name}
                  />
                  {/* Gradient overlay at bottom of image */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "60px",
                      background: "linear-gradient(to top, rgba(26,26,26,1) 0%, transparent 100%)",
                    }}
                  />
                  {/* Price badge */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      right: 10,
                      backgroundColor: "#FFC001",
                      color: "#000",
                      fontWeight: 700,
                      fontSize: "15px",
                      px: 1.5,
                      py: 0.4,
                      borderRadius: "8px",
                    }}
                  >
                    ${item.price}
                  </Box>
                  {/* Sex icon */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      backgroundColor: "rgba(0,0,0,0.6)",
                      borderRadius: "50%",
                      width: 30,
                      height: 30,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.sex === "male" ? (
                      <MaleIcon sx={{ color: "#4da6ff", fontSize: 18 }} />
                    ) : item.sex === "female" ? (
                      <FemaleIcon sx={{ color: "#ff69b4", fontSize: 18 }} />
                    ) : (
                      <FavoriteBorderIcon sx={{ color: "#FFC001", fontSize: 16 }} />
                    )}
                  </Box>
                  {/* Status badges */}
                  {item?.status === "sold" && (
                    <Chip
                      label="SOLD"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        backgroundColor: "#EF0107",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "11px",
                        height: "24px",
                      }}
                    />
                  )}
                  {item?.on_hold && (
                    <Chip
                      label="ON HOLD"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        backgroundColor: "#FFD300",
                        color: "#000",
                        fontWeight: 700,
                        fontSize: "11px",
                        height: "24px",
                      }}
                    />
                  )}
                  {item?.status === "nfs" && (
                    <Chip
                      label="NFS"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        backgroundColor: "#333",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "11px",
                        height: "24px",
                      }}
                    />
                  )}
                </Box>

                {/* Card content */}
                <Box sx={{ px: 1.5, py: 1.2, flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <Box>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "#fff",
                        lineHeight: 1.3,
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{
                        color: "#888",
                        fontSize: "12px",
                        mt: 0.3,
                      }}
                    >
                      {getCategoryName(item.category_id)}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
      </Container>
    </Box>
  );
};

const mapStateToProps = ({ Categories }) => ({
  classes: Categories.classes,
});

export default connect(mapStateToProps)(RecentListings);
